import React, { useState } from "react";
import { ListItem, Typography, Grid, Box } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import "../../payment/savedcard/SavedCard.css";
import Paper from "@material-ui/core/Paper";
import { CheckCircleOutlineRounded } from "@material-ui/icons";
import { FormatAmount, formatWordToCamelCase } from "../../../utils/Formatter";
import { getCardIconForScheme } from "../../../utils/BinUtils";

function parseDate(date) {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric"
  };
  const today = new Date(date);
  return today.toLocaleDateString("en-US", options);
}

const RecurringComponent = ({ recurring }) => {
  const {
    plan = {},
    status,
    initialChargeUrl,
    returnMethod,
    returnUrl,
    boundCard = {}
  } = recurring;
  const { scheme, maskedPan, title, expiry } = boundCard || {};

  const {
    type,
    amount,
    amountType,
    currency,
    initialAmount,
    startDate,
    endDate,
    every,
    unit,
    nbInstallments
  } = plan || {};
  const history = useHistory();

  const handleRedirect = () => {
    const chargeURL = `/charge/${initialChargeUrl.split("/").pop()}`;
    history.push(chargeURL);
  };

  const formatExpiryDate = input => {
    if (input.length !== 4) {
      throw new Error("Input should be exactly 4 digits in MMYY format.");
    }
    const month = input.slice(0, 2); // Extract first two digits (MM)
    const year = input.slice(2); // Extract last two digits (YY)
    return `${month}/${year}`;
  };

  const [checked, setChecked] = useState(true);

  const handleUserConsentChange = event => {
    setChecked(event.target.checked);
  };

  const hideButtonAndCheckbox = () => {
    return status !== "OPEN";
  };

  const buttonName = () => {
    if (initialAmount && initialAmount > 0) {
      return `Proceed to initial payment of ${FormatAmount(
        initialAmount,
        currency
      )}`;
    }
    return "Proceed to card verification";
  };

  const frequencyText = `every ${every} ${formatWordToCamelCase(unit)}`;

  const cardInfo = () => {
    const cardNumber = maskedPan;
    return (
      <>
        <div>
          <span className="saved-card-number">{cardNumber}</span>
          <span className="saved-card-exp-date">
            {formatExpiryDate(expiry)}
          </span>
        </div>
        <div>
          {title ? <span className="saved-card-title">{title}</span> : ""}
        </div>
      </>
    );
  };

  return (
    <div>
      <ListItem>
        <Paper style={{ padding: "6px 16px", width: "100%" }} elevation={3}>
          <ListItemText>
            <Typography
              style={{ textAlign: "center" }}
              variant="h6"
              component="h3"
            >
              {formatWordToCamelCase(type)}
              &nbsp;Mandate
            </Typography>

            <Grid
              container
              alignItems="center"
              style={{
                marginTop: "10px",
                letterSpacing: "1px",
                lineHeight: "3rem"
              }}
            >
              {/* Display only if initialAmount > 0 */}
              {initialAmount > 0 && (
                <>
                  <Grid item xs={7}>
                    <ListItemText>Initial Amount</ListItemText>
                  </Grid>
                  <Grid item xs={5} container alignItems="center">
                    {/* Amount Section */}
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center">
                        {FormatAmount(initialAmount, currency)}
                        {(status === "CLOSED" || status === "BOUND") && (
                          <CheckCircleOutlineRounded
                            style={{ color: "green", marginLeft: "5px" }}
                            sx={{ ml: 3 }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}

              <Grid item xs={7}>
                Recurring Amount :
              </Grid>
              <Grid item xs={5}>
                {FormatAmount(amount, currency)}
              </Grid>
              <Grid item xs={7}>
                Amount Type :
              </Grid>
              <Grid item xs={5}>
                {amountType}
              </Grid>

              <Grid item xs={7}>
                Frequency :
              </Grid>
              <Grid item xs={5}>
                {frequencyText}
              </Grid>
              <Grid item xs={7}>
                Start Date :
              </Grid>
              <Grid item xs={5}>
                {parseDate(startDate)}
              </Grid>

              <Grid item xs={7}>
                End Date :
              </Grid>
              <Grid item xs={5}>
                {endDate ? parseDate(endDate) : "Until cancelled"}
              </Grid>

              {/* installment */}
              {nbInstallments && (
                <>
                  <Grid item xs={7}>
                    No of Installments
                  </Grid>
                  <Grid item xs={5}>
                    {nbInstallments}
                  </Grid>
                </>
              )}
            </Grid>
          </ListItemText>
        </Paper>
      </ListItem>
      <ListItem
        style={{
          display: hideButtonAndCheckbox() ? "none" : "flex",
          alignItems: "center"
        }}
      >
        <Checkbox
          checked={checked}
          onChange={handleUserConsentChange}
          color="primary"
          inputProps={{ "aria-label": "User Consent Checkbox" }}
        />
        <ListItemText primary="I hereby agree for my card account to be debited as per the payment plan" />
      </ListItem>

      {boundCard && scheme && maskedPan && expiry && (
        <ListItem>
          <Paper style={{ padding: "6px 16px", width: "100%" }} elevation={3}>
            <Typography
              style={{ textAlign: "center" }}
              variant="h8"
              component="span"
            >
              Card registered for the subscription
            </Typography>
            <Grid
              container
              alignItems="center"
              style={{
                marginTop: "10px",
                letterSpacing: "1px",
                lineHeight: "3rem"
              }}
            >
              <Grid item xs={2}>
                <ListItemIcon className="saved-card-logo">
                  <img
                    src={getCardIconForScheme(scheme)}
                    alt="card-logo"
                    style={{ width: 50 }}
                  />
                </ListItemIcon>
              </Grid>
              <Grid item xs={8}>
                <ListItemText
                  primary={cardInfo(scheme, maskedPan, expiry, title)}
                />
              </Grid>
            </Grid>
          </Paper>
        </ListItem>
      )}

      <div
        style={{
          textAlign: "center",
          display: hideButtonAndCheckbox() ? "none" : "flex"
        }}
      >
        <Button
          id="pay-button"
          variant="outlined"
          color="primary"
          onClick={handleRedirect}
          disabled={!checked}
          style={{ width: "100%" }}
        >
          {buttonName()}
        </Button>
      </div>

      <div
        style={{
          textAlign: "center",
          display: status === "OPEN" ? "none" : "flex"
        }}
      >
        <Grid
          container
          alignItems="center"
          style={{
            marginTop: "10px",
            letterSpacing: "1px",
            lineHeight: "3rem"
          }}
        >
          <Grid item xs={12}>
            <form
              action={returnUrl}
              method={returnMethod}
              style={{ width: "100%" }}
              // ref={formRef}
            >
              {/* {getReturnFields()} */}
              <Button
                color="primary"
                type="submit"
                variant="outlined"
                style={{
                  marginTop: "15px",
                  marginBottom: "15px",
                  width: "80%"
                }}
              >
                Return to shop
              </Button>
            </form>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

RecurringComponent.propTypes = {
  recurring: PropTypes.shape({
    status: PropTypes.number.isRequired,
    initialChargeUrl: PropTypes.string.isRequired,
    returnUrl: PropTypes.string,
    returnMethod: PropTypes.string,
    testMode: PropTypes.bool.isRequired,
    plan: PropTypes.shape({
      type: PropTypes.string.isRequired,
      nbInstallments: PropTypes.string,
      every: PropTypes.string,
      unit: PropTypes.string,
      currency: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
      amountType: PropTypes.string.isRequired,
      endDate: PropTypes.string,
      startDate: PropTypes.string,
      initialAmount: PropTypes.number
    }).isRequired,
    boundCard: PropTypes.shape({
      id: PropTypes.string.isRequired,
      scheme: PropTypes.string.isRequired,
      maskedPan: PropTypes.string.isRequired,
      expiry: PropTypes.string.isRequired,
      title: PropTypes.string
    })
  }).isRequired
};

export default RecurringComponent;
